import axios from 'axios'

/**
 * ===============================================
 * Auth service
 * ===============================================
 *
 * Provides authentication properties and actions
 *
 * ===============================================
 */

export default {

  requestedPage: '',

  /**
   * Formats and stores the auth access in localStorage
   *
   * @param data
   */
  setAuth (data) {
    const now = new Date()

    data.expiration_date = new Date(
      now.setSeconds(now.getSeconds() + data.expires_in)
    )

    localStorage.setItem('auth', JSON.stringify(data))
  },

  /**
   * The requested route.
   *
   * @return string
   */
  getRequestedPage () {
    return this.requestedPage
  },

  /**
   * Stores the user info in localStorage
   *
   * @param user * { id, name, email, ... }
   */
  setUser (user) {
    localStorage.setItem('user', JSON.stringify(user))
  },

  /**
   * Gets the auth access in localStorage.
   *
   * @return * { token_type, access_token, refresh_token, expires_in, expiration_date }
   */
  getAuth () {
    return JSON.parse(localStorage.getItem('auth'))
  },

  /**
   * Gets the access_token from auth.
   *
   * @return string
   */
  getAccessToken () {
    return this.getAuth() ? this.getAuth().access_token : null
  },

  /**
   * Gets the refresh_token from auth.
   *
   * @return string
   */
  getRefreshToken () {
    return this.getAuth() ? this.getAuth().refresh_token : null
  },

  /**
   * Gets the expiration_date from auth.
   *
   */
  getTokenExpiration () {
    return this.getAuth() ? new Date(this.getAuth().expiration_date) : null
  },

  /**
   * Gets the user info from localStorage.
   *
   * @return * { id, name, email, ... }
   */
  getUser () {
    return JSON.parse(localStorage.getItem('user'))
  },

  /**
   * Set an axios instance with access_token as authorization header.
   *
   */
  api () {
    return axios.create({
      headers: {
        Authorization: `Bearer ${this.getAccessToken()}`
      }
    })
  },

  /**
   * Removes auth and user from localStorage.
   *
   */
  flush () {
    localStorage.removeItem('auth')
    localStorage.removeItem('user')
    localStorage.removeItem('trainer_id')
  },

  /**
   * Sends an login request to Auth API.
   *
   * @param credentials * { email, password }
   */
  login (credentials) {
    return axios.post(`${process.env.VUE_APP_AUTH_API}/api/login`, {
      email: credentials.email,
      password: credentials.password
    })
  },

  /**
   * Sends a logout request to Auth API.
   *
   */
  logout () {
    return this.api().post(`${process.env.VUE_APP_AUTH_API}/api/logout`)
  },

  /**
   * Sends a refresh token request to Auth API.
   *
   */
  async refreshToken () {
    await axios
      .post(`${process.env.VUE_APP_AUTH_API}/api/refresh-token`, {
        refresh_token: this.getRefreshToken()
      })
      .then(({ data }) => this.setAuth(data))
  },

  /**
   * Checks if token is expired.
   * An allowance of 1 minute is set
   * to prevent tokens expiring between API calls
   */
  tokenIsExpired () {
    const now = new Date()

    const refreshTime = now.setSeconds(
      now.getSeconds() + 60 * 5
    )

    return new Date(refreshTime) > new Date(this.getTokenExpiration())
  },

  /**
   * Sends an auth check request to Auth API.
   *
   * @return AxiosResponse<user>
   */
  async verifyAccessToken () {
    this.tokenIsExpired() && await this.refreshToken()

    return this.api().get(`${process.env.VUE_APP_AUTH_API}/api/user/auth`)
  },

  /**
   * Store trainer_id in local storage
   *
   */
  setTrainerId (email) {
    return this.api()
      .get(`${process.env.VUE_APP_MAIN_API}/api/trainer/search`, {
        params: {
          email: email,
          first: true
        }
      })
      .then(({ data }) => {
        localStorage.setItem('trainer_id', data.teacher_id)
        return data
      })
  },

  /**
   * Get the stored trainer_id
   *
   */
  getTrainerId () {
    return parseInt(localStorage.getItem('trainer_id'))
  },

  async refreshTrainerIdByToken() {
    return await this.verifyAccessToken()
      .then(({ data }) => {
        this.setTrainerId(data.email)
      })
  },

  /**
   * Check if user is a trainer
   *
   */
  isTrainer () {
    const trainer = this.getTrainerId()
    return !!trainer && trainer !== 'undefined'
  }
}
